/* eslint-disable react-hooks/exhaustive-deps */
import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AutoComplete, Button, Dropdown, Empty, message, Modal, Pagination, Select, Spin, Tooltip } from "antd";
import { ChevronDown, MoreVertical, Plus, X } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import axios from "axios";
import { AUTH_TOKEN, DOMAIN_ID, ORG_ID, REACT_APP_API } from "../../../constants";
import { logOutUser } from "../../../common";
import { apiBadRequest, apiGetSuccess, apiServerError, isEmpty } from "../../../utils/helpers";
import { dispatchGetAllDoamins, dispatchUpdateDomainEnforcement } from "../../../utils/apiservice";
import { useDebounce } from "../../../common/debounce";
import { AN_COLORS, AN_FONT, AN_MONO, anAlpha } from "../../../design-system/tokens";
import { useScrollbarWhileScrolling } from "../../../design-system/tableUi";

// A sensor that hasn't checked in within this window is stale, not live.
const SENSOR_STALE_MS = 15 * 60 * 1000;

// Shared tone triplets for the sensor, mode and protection cells — the same
// palette the old agent-status pill used, keyed by intent rather than by
// status string so all three cells stay in sync.
const TONE = {
  success: { bg: "var(--an-successBg)", fg: AN_COLORS.success, bd: "var(--an-successBd)" },
  warn:    { bg: "var(--an-warnBg)", fg: AN_COLORS.warn,    bd: "var(--an-warnBd)" },
  info:    { bg: "var(--an-infoBg)", fg: "var(--an-infoFg)",         bd: "var(--an-infoBd)" },
  neutral: { bg: AN_COLORS.bg, fg: AN_COLORS.muted, bd: AN_COLORS.line },
};

const pillStyle = (tone) => {
  const t = TONE[tone] || TONE.neutral;
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "2px 9px",
    borderRadius: 99,
    background: t.bg,
    color: t.fg,
    border: `1px solid ${t.bd}`,
    fontSize: 11.5,
    fontWeight: 500,
    whiteSpace: "nowrap",
  };
};

const Dot = ({ tone }) => (
  <span
    style={{
      width: 6,
      height: 6,
      borderRadius: "50%",
      flexShrink: 0,
      background: (TONE[tone] || TONE.neutral).fg,
    }}
  />
);

// Null-safe timestamp parser. Rejects unparseable values AND the Go zero time
// ("0001-01-01T00:00:00Z"), which is a truthy string and would otherwise
// render as a year-1 date.
const parseTs = (s) => {
  if (!s) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  if (d.getUTCFullYear() < 1971) return null;
  return d;
};

const isSensorLive = (sensor) => {
  const d = parseTs(sensor?.lastSeen);
  return !!d && Date.now() - d.getTime() < SENSOR_STALE_MS;
};

const relTime = (s) => {
  const d = parseTs(s);
  return d ? formatDistanceToNow(d, { addSuffix: true }) : "Never seen";
};

const TypeBadge = ({ children }) => (
  <span
    style={{
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: 5,
      background: AN_COLORS.tint,
      color: AN_COLORS.ink2,
      border: `1px solid ${AN_COLORS.line}`,
      fontFamily: AN_MONO,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: 0.4,
      textTransform: "uppercase",
      whiteSpace: "nowrap",
    }}
  >
    {children || "—"}
  </span>
);

// Configured intent, and the one setting on this screen an admin actually
// changes. In monitor mode the sensor observes and reports but never blocks and
// never pushes MFA — the tooltip is the whole point of the badge, since a
// correct policy in monitor mode looks like it does nothing.
//
// The mode lives server-side, so switching takes effect on the next
// authentication: nothing to re-download, no DC service to restart.
//
// Expect `mode: "enforce"` in sensor.yml on a DC even for a domain shown here as
// Monitoring. That is not a bug. To the sensor that value means "ask AuthNull on
// every authentication"; AuthNull then decides whether to challenge. The file
// carries a comment saying as much.
const MODE_TONE = {
  monitor: {
    tone: "neutral",
    label: "Monitoring",
    tip: "Authentications are evaluated and logged. Nothing is blocked and no MFA prompts are sent.",
  },
  enforce: {
    tone: "success",
    label: "Enforcing",
    tip: "MFA verdicts are applied. Users may be blocked.",
  },
};

const MODE_ORDER = ["monitor", "enforce"];

/**
 * The mode pill, as a dropdown. `pending` disables it while that row's switch is
 * in flight — which also keeps a second switch from aborting the first, since
 * dispatchUpdateDomainEnforcement shares one AbortController across calls.
 */
const ModeControl = ({ mode, pending, onSelect }) => {
  const m = MODE_TONE[mode];
  const tone = m ? m.tone : "neutral";

  const menu = {
    // An unrecognised mode leaves nothing selected, so a directory whose
    // enforcementMode the API hasn't returned yet can still be set.
    selectedKeys: m ? [mode] : [],
    items: MODE_ORDER.map((key) => ({
      key,
      label: (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
          <Dot tone={MODE_TONE[key].tone} />
          {MODE_TONE[key].label}
        </span>
      ),
    })),
    onClick: ({ key }) => {
      if (key !== mode) onSelect(key);
    },
  };

  const pill = (
    <button
      type="button"
      disabled={pending}
      style={{
        ...pillStyle(tone),
        cursor: pending ? "default" : "pointer",
        opacity: pending ? 0.55 : 1,
        gap: 5,
        paddingRight: 6,
      }}
    >
      {m && <Dot tone={tone} />}
      {m ? m.label : "—"}
      <ChevronDown size={12} strokeWidth={2.2} style={{ opacity: 0.7 }} />
    </button>
  );

  return (
    <Dropdown menu={menu} trigger={["click"]} placement="bottomLeft" disabled={pending}>
      {m ? <Tooltip title={m.tip}>{pill}</Tooltip> : pill}
    </Dropdown>
  );
};

// How many sensor hostnames get their own row before the rest collapse into
// "+N more". A domain can have several DCs; they must not become one dot.
const SENSOR_ROWS_SHOWN = 2;

const sensorLabel = (s) => s?.hostname || "unknown host";

const SensorRow = ({ sensor }) => {
  const tone = isSensorLive(sensor) ? "success" : "warn";
  const d = parseTs(sensor?.lastSeen);
  const tip = [
    d ? `Last seen ${d.toISOString()}` : "Never seen",
    Number.isFinite(sensor?.eventCount) ? `${sensor.eventCount.toLocaleString()} events` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <Tooltip title={tip}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
        <Dot tone={tone} />
        <span
          style={{
            fontFamily: AN_MONO,
            fontSize: 11.5,
            color: AN_COLORS.ink3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {sensorLabel(sensor)}
        </span>
      </div>
    </Tooltip>
  );
};

const SensorsCell = ({ sensors }) => {
  if (!sensors.length) {
    return (
      <span style={pillStyle("neutral")}>
        <Dot tone="neutral" />
        No sensor detected
      </span>
    );
  }

  const shown = sensors.slice(0, SENSOR_ROWS_SHOWN);
  const rest = sensors.slice(SENSOR_ROWS_SHOWN);
  const liveCount = sensors.filter(isSensorLive).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
      {shown.map((s, i) => (
        <SensorRow key={s?.hostname || i} sensor={s} />
      ))}

      {/* One sensor: show when it last checked in. Several: a liveness roll-up,
          since a per-host timestamp on every row would double the row height. */}
      <div style={{ fontSize: 11, color: AN_COLORS.muted, paddingLeft: 12 }}>
        {sensors.length === 1
          ? relTime(sensors[0]?.lastSeen)
          : `${liveCount} of ${sensors.length} live`}
      </div>

      {rest.length > 0 && (
        <Tooltip
          title={
            <div>
              {rest.map((s, i) => (
                <div key={s?.hostname || i}>
                  {sensorLabel(s)} — {relTime(s?.lastSeen)}
                </div>
              ))}
            </div>
          }
        >
          <span
            style={{
              fontSize: 11,
              color: AN_COLORS.muted,
              fontFamily: AN_MONO,
              cursor: "default",
              paddingLeft: 12,
            }}
          >
            +{rest.length} more
          </span>
        </Tooltip>
      )}
    </div>
  );
};

// Effective reality, as opposed to the configured mode: a directory is only
// protected if it is enforcing AND something is actually reporting.
const protectionState = (sensors, mode) => {
  if (!sensors.length) return { tone: "neutral", label: "Not protected", reason: "no sensor reporting" };
  if (mode !== "enforce") return { tone: "info", label: "Monitoring only", reason: "not enforcing yet" };
  if (sensors.some(isSensorLive)) return { tone: "success", label: "Protected", reason: null };
  // Enforcing, but every sensor is stale — nothing is reaching us to enforce on.
  return { tone: "warn", label: "Not protected", reason: "sensor not reporting" };
};

// Rendered as a plain status readout, not a pill: the Mode column already
// carries a pill, and two pills side by side read as two buttons. A dot plus
// label matches the sensor cell's vocabulary and makes it clear this is state,
// not a control — it is deliberately not interactive.
const ProtectionCell = ({ sensors, mode }) => {
  const s = protectionState(sensors, mode);
  const tone = TONE[s.tone] || TONE.neutral;
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 7, minWidth: 0 }}>
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          flexShrink: 0,
          marginTop: 5,
          background: tone.fg,
        }}
      />
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 12.5,
            fontWeight: 500,
            color: AN_COLORS.ink2,
            whiteSpace: "nowrap",
            lineHeight: 1.35,
          }}
        >
          {s.label}
        </div>
        {s.reason && (
          <div style={{ fontSize: 11, color: AN_COLORS.muted, lineHeight: 1.4, marginTop: 1 }}>
            {s.reason}
          </div>
        )}
      </div>
    </div>
  );
};

const AppliedChip = ({ children, onClear }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 4px 4px 10px",
      borderRadius: 99,
      background: AN_COLORS.primaryL,
      border: `1px solid ${anAlpha(AN_COLORS.primary, 13)}`,
      color: AN_COLORS.primary,
      fontSize: 12,
      fontWeight: 500,
    }}
  >
    {children}
    <button
      type="button"
      onClick={onClear}
      aria-label="Clear filter"
      style={{
        border: "none",
        background: "transparent",
        cursor: "pointer",
        color: AN_COLORS.primary,
        padding: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 18,
        height: 18,
        borderRadius: 99,
      }}
    >
      <X size={12} strokeWidth={2.4} />
    </button>
  </span>
);

const FilterField = ({ label, children }) => (
  <div>
    <div
      style={{
        fontSize: 11.5,
        fontWeight: 500,
        color: AN_COLORS.ink2,
        marginBottom: 6,
        fontFamily: AN_FONT,
      }}
    >
      {label}
    </div>
    {children}
  </div>
);

const iconButtonStyle = () => ({
  width: 28,
  height: 28,
  borderRadius: 5,
  border: "none",
  background: "transparent",
  color: AN_COLORS.muted,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
});

// lastSyncTime is null until the first sync, and the backend may send the Go
// zero time instead. Both mean "never" — parseTs collapses them to null so
// neither can reach toLocaleString and render as a year-1 date.
const fmtSync = (dt) => {
  const d = parseTs(dt);
  if (!d) return "Never synced";
  return d.toLocaleString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const PAGESIZE = 10;

export default function ProvidersDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const [notification, setNotification] = message.useMessage();
  const tableScrollRef = useScrollbarWhileScrolling();

  const [directorySearch, setDirectorySearch] = useState("");
  const directoryDebouncedValue = useDebounce(directorySearch, 700);

  const [selectedDomain, setSelectedDomain] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  // { row, next } while the mode-change confirmation is open; the row id whose
  // switch is in flight.
  const [modeConfirm, setModeConfirm] = useState(null);
  const [pendingModeId, setPendingModeId] = useState(null);
  const [isDataLoading, setIsDataLoading] = useState(false);
  // Row whose enrollment panel is open. One at a time: the panel fetches a
  // summary per directory, and only the open one should be doing that.

  const [dataTableValues, setDataTableValues] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationParams, setPaginationParams] = useState({ current: 0, pageSize: PAGESIZE });
  const { filter } = location?.state || {};

  const [filterParams, setFilterParams] = useState({
    directoryName: "",
    identitySource: "",
    integrationType: "",
    status: "",
  });

  const setFilter = (key, value) =>
    setFilterParams((prev) => ({ ...prev, [key]: value || "" }));

  const clearAllFilters = () => {
    setFilterParams({ directoryName: "", identitySource: "", integrationType: "", status: "" });
    setDirectorySearch("");
  };

  const directoryOptions = useMemo(() => {
    if (isEmpty(directorySearch)) return [];
    const q = directorySearch.toLowerCase();
    return dataTableValues
      .filter((r) => r.directoryName && r.directoryName.toLowerCase().includes(q))
      .map((r) => ({ value: r.directoryName, label: r.directoryName }));
  }, [dataTableValues, directorySearch]);

  useEffect(() => {
    if (filter) {
      setFilterParams((prev) => ({ ...prev, ...filter }));
    }
  }, [filter]);

  useEffect(() => {
    if (paginationParams.current !== 0) {
      setPaginationParams({ current: 0, pageSize: PAGESIZE });
    }
  }, [filterParams]);

  useEffect(() => {
    fetchIdentityProvider();
  }, [paginationParams, filterParams, directoryDebouncedValue]);

  const handleApiFailure = ({ message: m, isLogout = false }) => {
    setIsDataLoading(false);
    notification.error({ content: m });
    if (isLogout) logOutUser();
  };
  const handleApiError = ({ message: m }) => {
    setIsDataLoading(false);
    notification.error({ content: m });
  };

  const fetchIdentityProvider = () => {
    setIsDataLoading(true);
    dispatchGetAllDoamins({
      payload: {
        tenantId: DOMAIN_ID(),
        pageNo: paginationParams.current,
        pageSize: paginationParams.pageSize,
        filters: filterParams,
        orgId: ORG_ID(),
      },
      onSuccess: (res) => {
        setIsDataLoading(false);
        const updatedData = (res?.domains || []).filter((item) => item?.status !== "Deleted");
        const ADData = updatedData.map((item, i) => {
          const domain = !isEmpty(item?.domain)
            ? item.domain
            : (!isEmpty(item?.dc1) && !isEmpty(item?.dc2) ? `${item.dc1}.${item.dc2}` : "—");
          return {
            id: i + 1,
            ...item,
            domain,
            // A domain can report several DCs, or none at all.
            sensors: Array.isArray(item?.sensors) ? item.sensors : [],
            mode: String(item?.enforcementMode || "").toLowerCase(),
            IntegrationType: isEmpty(item?.integration_type) ? (item.adMfa === 0 ? "Agentbased" : "Agentless") : item?.integration_type,
            IdentitySource: isEmpty(item?.identity_source) ? "AD" : item?.identity_source,
            // lastSyncTime is deliberately left raw here so staleness logic can
            // still read it; formatting happens in the cell.
          };
        });
        setDataTableValues(ADData);
        setTotalCount(res?.totalCount || 0);
      },
      onFailure: handleApiFailure,
      onError: handleApiError,
    });
  };


  const handleDeleteConfirmed = async () => {
    if (!selectedDomain) return;
    const payload = { adId: selectedDomain.id, orgId: ORG_ID(), tenantId: DOMAIN_ID() };
    try {
      const { status } = await axios.post(`${REACT_APP_API}/ad/DeleteDomain`, payload, {
        headers: { "X-Authorization": AUTH_TOKEN(), withCredentials: true },
      });
      if (apiGetSuccess({ status })) {
        notification.success({ content: "Domain deleted successfully!" });
        fetchIdentityProvider();
        return;
      }
    } catch ({ response, status }) {
      if (apiBadRequest({ status })) return handleApiFailure({ message: "Please Check the API URL" });
      if (apiServerError({ status })) return handleApiError({ message: response?.data?.error });
      if (response?.data?.message === "Invalid credentials" || status === 401)
        return handleApiFailure({ message: "Credentials are invalid", isLogout: true });
    } finally {
      setDeleteModalOpen(false);
      setSelectedDomain(null);
    }
  };

  const confirmModeChange = () => {
    if (!modeConfirm) return;
    const { row, next } = modeConfirm;
    const label = row?.directoryName || row?.domain || "this directory";
    setModeConfirm(null);
    setPendingModeId(row.id);
    dispatchUpdateDomainEnforcement({
      payload: {
        tenantId: DOMAIN_ID(),
        orgId: ORG_ID(),
        adId: row.id,
        enforcementMode: next,
      },
      onSuccess: () => {
        setPendingModeId(null);
        // Patch the row rather than refetching: a full reload would drop the
        // table into its spinner and reset scroll for a one-field change. The
        // Protection column recomputes from row.mode, so it follows for free.
        setDataTableValues((rows) =>
          rows.map((r) => (r.id === row.id ? { ...r, mode: next, enforcementMode: next } : r))
        );
        notification.success({
          content: next === "enforce"
            ? `${label} is now enforcing MFA.`
            : `${label} is back to monitoring only.`,
        });
      },
      onFailure: (e) => { setPendingModeId(null); handleApiFailure(e); },
      onError: (e) => { setPendingModeId(null); handleApiError(e); },
    });
  };

  const rowMenu = (row) => ({
    items: [
      { key: "view",       label: "View users" },
      { type: "divider" },
      { key: "delete",     label: <span style={{ color: AN_COLORS.danger }}>Delete</span> },
    ],
    onClick: ({ key }) => {
      if (key === "view") {
        if (row?.directoryName) navigate("/pam/users", { state: { filter: { directoryName: row.directoryName } } });
      } else if (key === "delete") {
        setSelectedDomain(row);
        setDeleteModalOpen(true);
      }
    },
  });

  const addIdentityProviderItems = [
    {
      key: "ad",
      label: (
        <Link to={{ pathname: "/pam/directory/addDirectory/step" }} state={{ isAD: true }}>
          Active Directory
        </Link>
      ),
    },
    {
      key: "entra",
      label: (
        <Link to={{ pathname: "/pam/directory/addDirectory/step" }} state={{ isEntra: true }}>
          EntraID
        </Link>
      ),
    },
  ];

  const appliedFilters = [];
  if (filterParams.directoryName)
    appliedFilters.push({
      key: "directoryName",
      label: `Directory: ${filterParams.directoryName}`,
      onClear: () => { setFilter("directoryName", ""); setDirectorySearch(""); },
    });
  if (filterParams.identitySource)
    appliedFilters.push({
      key: "identitySource",
      label: `Source: ${filterParams.identitySource}`,
      onClear: () => setFilter("identitySource", ""),
    });
  if (filterParams.integrationType)
    appliedFilters.push({
      key: "integrationType",
      label: `Integration: ${filterParams.integrationType}`,
      onClear: () => setFilter("integrationType", ""),
    });
  if (filterParams.status)
    appliedFilters.push({
      key: "status",
      label: `Status: ${filterParams.status}`,
      onClear: () => setFilter("status", ""),
    });

  return (
    <div
      style={{
        minHeight: "calc(100vh - 64px)",
        background: AN_COLORS.bgWarm,
        color: AN_COLORS.ink,
        fontFamily: AN_FONT,
        padding: "22px 28px",
      }}
    >
      {setNotification}

      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          marginBottom: 18,
          gap: 16,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontFamily: AN_MONO,
              fontSize: 10.5,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: AN_COLORS.muted,
              marginBottom: 6,
            }}
          >
            Identity · Sources
          </div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: -0.4 }}>
            Identity Sources
          </h1>
          <div style={{ fontSize: 13, color: AN_COLORS.muted, marginTop: 4 }}>
            {totalCount || dataTableValues.length} directories. Where your users come from: Active Directory and Entra ID today, more providers to follow.
          </div>
        </div>
        <Dropdown
          menu={{ items: addIdentityProviderItems }}
          placement="bottomRight"
          arrow={{ pointAtCenter: true }}
        >
          <Button type="primary" icon={<Plus size={13} strokeWidth={2.2} />}>
            Add identity source
          </Button>
        </Dropdown>
      </div>

      {/* Filters card */}
      <div
        style={{
          background: "var(--an-surface)",
          border: `1px solid ${AN_COLORS.line}`,
          borderRadius: 10,
          padding: 14,
          marginBottom: 14,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.6fr 1fr 1fr 1fr",
            gap: 12,
            alignItems: "end",
          }}
        >
          <FilterField label="Directory name">
            <AutoComplete
              value={filterParams.directoryName}
              options={directoryOptions}
              onSearch={(v) => { setDirectorySearch(v); setFilter("directoryName", v); }}
              onSelect={(v) => setFilter("directoryName", v)}
              onChange={(v) => setFilter("directoryName", v)}
              placeholder="Search directory"
              allowClear
              style={{ width: "100%", height: 36 }}
            />
          </FilterField>
          <FilterField label="Identity source">
            <Select
              value={filterParams.identitySource || undefined}
              onChange={(v) => setFilter("identitySource", v)}
              placeholder="All sources"
              allowClear
              style={{ width: "100%", height: 36 }}
              options={[
                { value: "AD",      label: "AD" },
                { value: "EntraID", label: "EntraID" },
              ]}
            />
          </FilterField>
          <FilterField label="Integration type">
            <Select
              value={filterParams.integrationType || undefined}
              onChange={(v) => setFilter("integrationType", v)}
              placeholder="All types"
              allowClear
              style={{ width: "100%", height: 36 }}
              options={[
                { value: "Agentless",  label: "Agentless" },
                { value: "AgentBased", label: "Agent-based" },
              ]}
            />
          </FilterField>
          <FilterField label="Status">
            <Select
              value={filterParams.status || undefined}
              onChange={(v) => setFilter("status", v)}
              placeholder="All statuses"
              allowClear
              style={{ width: "100%", height: 36 }}
              options={[
                { value: "Active",  label: "Active" },
                { value: "Pending", label: "Inactive" },
              ]}
            />
          </FilterField>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginTop: 12,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: 12.5, color: AN_COLORS.ink2, fontWeight: 500 }}>
            Applied:
          </span>
          {appliedFilters.length === 0 ? (
            <span style={{ fontSize: 12.5, color: AN_COLORS.muted }}>None</span>
          ) : (
            appliedFilters.map((f) => (
              <AppliedChip key={f.key} onClear={f.onClear}>{f.label}</AppliedChip>
            ))
          )}
          {appliedFilters.length > 0 && (
            <button
              type="button"
              onClick={clearAllFilters}
              style={{
                border: "none",
                background: "transparent",
                color: AN_COLORS.primary,
                cursor: "pointer",
                fontSize: 12.5,
                fontWeight: 500,
                padding: 0,
              }}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <Spin spinning={isDataLoading} tip="Loading…">
      {/* Ten fixed-width columns overflow narrow viewports; an-scroll-x scrolls
          rather than clipping the Action column, and only shows its scrollbar
          while the user is scrolling. */}
      <div
        ref={tableScrollRef}
        className="an-scroll-x"
        style={{
          background: AN_COLORS.surface,
          border: `1px solid ${AN_COLORS.line}`,
          borderRadius: 10,
          minHeight: isDataLoading ? 200 : undefined,
        }}
      >
        {dataTableValues.length === 0 && !isDataLoading ? (
          <div style={{ padding: 48 }}>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No directories found." />
          </div>
        ) : dataTableValues.length > 0 ? (
          <table
            style={{
              width: "100%",
              // Floor matching the sum of the column widths below, so the
              // fixed layout stays honest and the wrapper scrolls instead.
              minWidth: 1245,
              borderCollapse: "collapse",
              tableLayout: "fixed",
              fontSize: 13,
            }}
          >
            <thead>
              <tr
                style={{
                  background: AN_COLORS.bg,
                  fontFamily: AN_MONO,
                  fontSize: 10.5,
                  letterSpacing: 1.1,
                  textTransform: "uppercase",
                  color: AN_COLORS.muted,
                }}
              >
                <th style={{ padding: "10px 16px", textAlign: "left", minWidth: 170 }}>Directory</th>
                <th style={{ padding: "10px 12px", textAlign: "left", width: 105 }}>Integration</th>
                <th style={{ padding: "10px 12px", textAlign: "left", width: 95 }}>Source</th>
                <th style={{ padding: "10px 12px", textAlign: "left", minWidth: 150 }}>Domain</th>
                <th style={{ padding: "10px 12px", textAlign: "left", width: 200 }}>Sensors</th>
                <th style={{ padding: "10px 12px", textAlign: "left", width: 130 }}>Mode</th>
                <th style={{ padding: "10px 12px", textAlign: "left", width: 175 }}>Protection</th>
                <th style={{ padding: "10px 12px", textAlign: "left", width: 150 }}>Last Sync</th>
                <th style={{ padding: "10px 16px", textAlign: "right", width: 70 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {dataTableValues.map((row) => {
                return (
                <Fragment key={row.id}>
                <tr style={{ borderTop: `1px solid ${AN_COLORS.line2}` }}>
                  <td style={{ padding: "12px 16px", color: AN_COLORS.ink, fontWeight: 500 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={row.directoryName}>
                        {row.directoryName || "—"}
                      </span>
                    </span>
                  </td>
                  <td style={{ padding: "12px" }}>
                    <TypeBadge>{row.IntegrationType}</TypeBadge>
                  </td>
                  <td style={{ padding: "12px" }}>
                    <TypeBadge>{row.IdentitySource}</TypeBadge>
                  </td>
                  <td
                    style={{
                      padding: "12px",
                      fontFamily: AN_MONO,
                      fontSize: 12,
                      color: AN_COLORS.ink3,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={row.domain}
                  >
                    {row.domain}
                  </td>
                  <td style={{ padding: "12px" }}>
                    <SensorsCell sensors={row.sensors} />
                  </td>
                  <td style={{ padding: "12px" }}>
                    <ModeControl
                      mode={row.mode}
                      pending={pendingModeId === row.id}
                      onSelect={(next) => setModeConfirm({ row, next })}
                    />
                  </td>
                  <td style={{ padding: "12px" }}>
                    <ProtectionCell sensors={row.sensors} mode={row.mode} />
                  </td>
                  <td
                    style={{
                      padding: "12px",
                      fontFamily: AN_MONO,
                      fontSize: 12,
                      color: AN_COLORS.muted,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {fmtSync(row.lastSyncTime)}
                  </td>
                  <td style={{ padding: "8px 16px", textAlign: "right" }}>
                    <Dropdown menu={rowMenu(row)} placement="bottomRight" trigger={["click"]}>
                      <button style={iconButtonStyle()} aria-label="Row actions">
                        <MoreVertical size={16} />
                      </button>
                    </Dropdown>
                  </td>
                </tr>
                </Fragment>
                );
              })}
            </tbody>
          </table>
        ) : null}
      </div>
      </Spin>

      {totalCount > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 12,
            marginTop: 14,
          }}
        >
          <Pagination
            current={paginationParams.current + 1}
            pageSize={paginationParams.pageSize}
            total={totalCount}
            showSizeChanger={false}
            onChange={(page, pageSize) =>
              setPaginationParams({ current: page - 1, pageSize })
            }
          />
        </div>
      )}

      <Modal
        open={!!modeConfirm}
        title={
          modeConfirm?.next === "enforce"
            ? "Start enforcing MFA?"
            : "Stop enforcing MFA?"
        }
        onOk={confirmModeChange}
        onCancel={() => setModeConfirm(null)}
        okText={modeConfirm?.next === "enforce" ? "Start enforcing" : "Stop enforcing"}
        okButtonProps={{ danger: modeConfirm?.next !== "enforce" }}
      >
        {modeConfirm?.next === "enforce" ? (
          <>
            Real logins on{" "}
            <strong style={{ color: AN_COLORS.ink }}>
              {modeConfirm?.row?.directoryName || modeConfirm?.row?.domain}
            </strong>{" "}
            will start being challenged for MFA, and may be blocked. Takes effect on the next
            authentication.
          </>
        ) : (
          <>
            Logins on{" "}
            <strong style={{ color: AN_COLORS.ink }}>
              {modeConfirm?.row?.directoryName || modeConfirm?.row?.domain}
            </strong>{" "}
            will no longer be challenged for MFA.
          </>
        )}
      </Modal>

      <Modal
        open={deleteModalOpen}
        title="Delete directory?"
        onOk={handleDeleteConfirmed}
        onCancel={() => setDeleteModalOpen(false)}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        Are you sure you want to delete{" "}
        <strong style={{ color: AN_COLORS.ink }}>
          {selectedDomain?.directoryName}
        </strong>
        ?
      </Modal>
    </div>
  );
}
